import React, { useState, useEffect } from "react";
import { FileCheck, CheckCircle, AlertCircle, Eye, Loader2 } from "lucide-react";
import {
  getEventRequiredDocuments,
  getVendorAcknowledgment,
  getVendorSignature,
  acknowledgeDocument,
  REQUIRED_DOCUMENT_TYPE_LABELS
} from "../../../services/EventDocumentService";
import {
  createNotification,
  NOTIFICATION_TYPES,
} from "../../../services/PebbleNotificationService";
import DocumentSigningModal from "./DocumentSigningModal";

const RequiredDocumentsStep = ({ eventId, vendorId, vendorName, onComplete }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [acknowledgedDocs, setAcknowledgedDocs] = useState({});
  const [acknowledging, setAcknowledging] = useState(null);
  const [signingDoc, setSigningDoc] = useState(null);
  const [signature, setSignature] = useState('');

  useEffect(() => {
    if (eventId && vendorId) {
      loadDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, vendorId]);

  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      const result = await getEventRequiredDocuments(eventId);
      if (result.success) {
        const requiredDocs = result.documents.filter(d => d.isRequired && d.requiresAcknowledgment !== false);
        setDocuments(requiredDocs);
        
        const ackStatus = {};
        for (const doc of requiredDocs) {
          if (doc.requiresSignature && doc.signatureFields?.length > 0) {
            // Pro signing flow: check vendorSignatures subcollection
            const sigResult = await getVendorSignature(eventId, doc.id, vendorId);
            ackStatus[doc.id] = sigResult.signed;
          } else {
            const ackResult = await getVendorAcknowledgment(eventId, doc.id, vendorId);
            ackStatus[doc.id] = ackResult.acknowledged;
          }
        }
        setAcknowledgedDocs(ackStatus);
        
        const allAck = requiredDocs.length === 0 || requiredDocs.every(doc => ackStatus[doc.id]);
        if (allAck && onComplete) {
          onComplete();
        }
      }
    } catch (err) {
      console.error('Error loading required documents:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcknowledge = async (doc) => {
    if (doc.requiresSignature && doc.signatureFields?.length > 0) {
      // Pro flow: open full canvas signing modal
      setSigningDoc(doc);
      return;
    }
    if (doc.requiresSignature) {
      // Legacy / no fields placed yet: fall back to text-input signing
      setSigningDoc(doc);
      return;
    }
    
    setAcknowledging(doc.id);
    try {
      const result = await acknowledgeDocument(eventId, doc.id, vendorId, vendorName);
      if (result.success) {
        setAcknowledgedDocs(prev => ({ ...prev, [doc.id]: true }));
        checkCompletion({ ...acknowledgedDocs, [doc.id]: true });
        // Notify EO
        try {
          if (doc.organizerId) {
            await createNotification({
              recipientId: doc.organizerId,
              recipientType: 'eo',
              type: NOTIFICATION_TYPES.DOCUMENT_ACKNOWLEDGED,
              title: 'Document acknowledged',
              message: `${vendorName} acknowledged "${doc.title}".`,
              eventId,
              documentId: doc.id,
              linkPath: `/eo/events/${eventId}`,
            });
          }
        } catch (_) { /* non-blocking */ }
      }
    } catch (err) {
      console.error('Error acknowledging document:', err);
    } finally {
      setAcknowledging(null);
    }
  };

  const handleSignAndAcknowledge = async () => {
    if (!signingDoc || !signature.trim()) return;

    setAcknowledging(signingDoc.id);
    try {
      const result = await acknowledgeDocument(eventId, signingDoc.id, vendorId, vendorName, signature);
      if (result.success) {
        setAcknowledgedDocs(prev => ({ ...prev, [signingDoc.id]: true }));
        checkCompletion({ ...acknowledgedDocs, [signingDoc.id]: true });
        // Notify EO
        try {
          if (signingDoc.organizerId) {
            await createNotification({
              recipientId: signingDoc.organizerId,
              recipientType: 'eo',
              type: NOTIFICATION_TYPES.DOCUMENT_SIGNED,
              title: 'Document signed',
              message: `${vendorName} signed "${signingDoc.title}".`,
              eventId,
              documentId: signingDoc.id,
              linkPath: `/eo/events/${eventId}`,
            });
          }
        } catch (_) { /* non-blocking */ }
        setSigningDoc(null);
        setSignature('');
      }
    } catch (err) {
      console.error('Error signing document:', err);
    } finally {
      setAcknowledging(null);
    }
  };

  const checkCompletion = (ackStatus) => {
    const allAcknowledged = documents.every(doc => ackStatus[doc.id]);
    if (allAcknowledged && onComplete) {
      onComplete();
    }
  };

  const allAcknowledged = documents.length === 0 || documents.every(doc => acknowledgedDocs[doc.id]);
  const acknowledgedCount = Object.values(acknowledgedDocs).filter(Boolean).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 transition-opacity duration-300">
        <p className="text-sm text-grey-500">Loading documents...</p>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
        <h3 className="font-semibold text-green-800 mb-1">No Required Documents</h3>
        <p className="text-sm text-green-600">This event doesn't have any documents that require your acknowledgment.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-grey-900">Required Documents</h3>
          <p className="text-sm text-grey-600">Please review and acknowledge the following documents to complete your registration.</p>
        </div>
        <div className="text-sm text-grey-500">
          {acknowledgedCount} of {documents.length} completed
        </div>
      </div>

      {!allAcknowledged && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-yellow-800">Action Required</p>
            <p className="text-xs text-yellow-600">You must acknowledge all required documents to complete your registration.</p>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {documents.map((doc) => {
          const isAcknowledged = acknowledgedDocs[doc.id];
          const isProcessing = acknowledging === doc.id;
          
          return (
            <div 
              key={doc.id} 
              className={`border rounded-xl p-4 transition ${
                isAcknowledged 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-white border-grey-200'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isAcknowledged ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    {isAcknowledged ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <FileCheck className="w-5 h-5 text-gray-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-grey-900">{doc.title}</h4>
                      {doc.requiresSignature && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                          Signature Required
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-grey-500 mt-0.5">
                      {REQUIRED_DOCUMENT_TYPE_LABELS[doc.type] || doc.type}
                    </p>
                    {doc.description && (
                      <p className="text-sm text-grey-600 mt-2">{doc.description}</p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 flex-shrink-0">
                  <a
                    href={doc.downloadURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-grey-600 hover:text-grey-800 border border-grey-300 hover:bg-grey-50 rounded-lg transition"
                  >
                    <Eye className="w-4 h-4" />
                    View
                  </a>
                  {isAcknowledged ? (
                    <span className="flex items-center gap-1 px-3 py-1.5 text-sm text-green-700 bg-green-100 rounded-lg">
                      <CheckCircle className="w-4 h-4" />
                      Acknowledged
                    </span>
                  ) : (
                    <button
                      onClick={() => handleAcknowledge(doc)}
                      disabled={isProcessing}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-pebble-red hover:bg-red-700 rounded-lg transition disabled:opacity-50"
                    >
                      {isProcessing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          {doc.requiresSignature ? 'Sign & Acknowledge' : 'Acknowledge'}
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {allAcknowledged && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle className="w-6 h-6 text-green-600" />
          <div>
            <p className="font-medium text-green-800">All documents acknowledged</p>
            <p className="text-sm text-green-600">You have completed all required document acknowledgments.</p>
          </div>
        </div>
      )}

      {signingDoc && signingDoc.signatureFields?.length > 0 ? (
        <DocumentSigningModal
          eventId={eventId}
          document={signingDoc}
          vendorId={vendorId}
          vendorName={vendorName}
          onComplete={() => {
            setAcknowledgedDocs(prev => ({ ...prev, [signingDoc.id]: true }));
            checkCompletion({ ...acknowledgedDocs, [signingDoc.id]: true });
            setSigningDoc(null);
          }}
          onClose={() => setSigningDoc(null)}
        />
      ) : signingDoc ? (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-grey-200">
              <h3 className="text-lg font-semibold text-grey-900">Sign Document</h3>
              <p className="text-sm text-grey-500 mt-1">{signingDoc.title}</p>
            </div>

            <div className="p-6">
              <p className="text-sm text-grey-600 mb-4">
                By signing below, you acknowledge that you have read and agree to the terms outlined in this document.
              </p>
              <div>
                <label className="block text-sm font-medium text-grey-700 mb-2">Type your full legal name as signature</label>
                <input
                  type="text"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder="Enter your full name"
                  className="w-full px-4 py-3 border border-grey-300 rounded-lg focus:ring-2 focus:ring-pebble-red focus:border-transparent text-lg"
                  style={{ fontFamily: "'Brush Script MT', cursive" }}
                />
              </div>
              <p className="text-xs text-grey-500 mt-3">
                This electronic signature has the same legal effect as a handwritten signature.
              </p>
            </div>

            <div className="p-6 border-t border-grey-200 flex gap-3">
              <button
                onClick={() => { setSigningDoc(null); setSignature(''); }}
                className="flex-1 px-4 py-2 border border-grey-300 text-grey-700 rounded-lg hover:bg-grey-50 transition font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSignAndAcknowledge}
                disabled={!signature.trim() || acknowledging}
                className="flex-1 px-4 py-2 bg-pebble-red text-white rounded-lg hover:bg-red-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {acknowledging ? 'Signing...' : 'Sign & Acknowledge'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default RequiredDocumentsStep;
